import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { BehaviorSubject, Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { Configuration, notifications } from 'api/notifications';
import { Empty } from './models';

export type Loading = ['loading'];
const Loading: Loading = ['loading'];

export type Loaded = ['loaded', Configuration[]];
const Loaded = (configurations: Configuration[]): Loaded => [
  'loaded',
  configurations,
];

export type Content = Empty | Loading | Loaded;

type UpdateConfiguration = (config: Configuration) => void;

interface ConfigurationStore {
  update: MutableRefObject<UpdateConfiguration>;
  content: Content;
}

export const useConfigurationsStore = (
  isCustomerConfig: boolean | undefined,
): ConfigurationStore => {
  const [content, setContent] = useState<Content>(Empty);
  const update = useRef<UpdateConfiguration>(() => {});

  const notificationConfig = useMemo(
    () =>
      isCustomerConfig
        ? notifications.customerConfiguration
        : notifications.configuration,
    [isCustomerConfig],
  );

  useEffect(() => {
    const subscriptions: Subscription[] = [];
    const content$ = new BehaviorSubject<Content>(Empty);

    subscriptions.push(content$.pipe(skip(1)).subscribe(setContent));

    content$.next(Loading);

    subscriptions.push(
      notificationConfig.get().subscribe(
        (results) => {
          content$.next(Loaded(results));
          update.current = (updated) => {
            const previous = content$.value;

            if (previous[0] !== 'loaded') {
              return;
            }
            const previousConfiguration = previous[1].find(
              (x) => x.eventType === updated.eventType,
            )!;

            content$.next(
              Loaded(
                previous[1]
                  .filter((x) => x.eventType !== updated.eventType)
                  .concat(updated),
              ),
            );
            subscriptions.push(
              notificationConfig.update(updated).subscribe(
                () => {},
                () => {
                  const current = content$.value;

                  if (current[0] === 'empty' || current[0] === 'loading') {
                    return;
                  }
                  content$.next(
                    Loaded(
                      current[1]
                        .filter((x) => x.eventType !== updated.eventType)
                        .concat(previousConfiguration),
                    ),
                  );
                },
              ),
            );
          };
        },
        () => {
          content$.next(Empty);
        },
      ),
    );

    return () => subscriptions.forEach((s) => s.unsubscribe());
  }, [notificationConfig, setContent]);

  return { content, update };
};
